import React, { useCallback, useMemo, useState, useEffect, useContext, useLayoutEffect } from "react";
import PropTypes from "prop-types";

import Dropdown, { OPTION_DISPLAY } from "@acadia-ui/dropdown";
import Button, { BUTTON_DISPLAY } from "@acadia-ui/button";

import { ViewMasterCampaignsContext } from "../index";
import { KeyValueFiltersContext } from "./useKeyValueFilters";

export default function KeyValueFilter({ position, totalNumFilters, uuid }) {
	const { dataKeyDataValueFilters } = useContext(ViewMasterCampaignsContext);

	const { value: allValues, setValue, handleAdd, handleDelete } = useContext(KeyValueFiltersContext);

	const [dataKey, setDataKey] = useState(null);
	const [dataValues, setDataValues] = useState([]);

	useLayoutEffect(() => {
		if (uuid && Object.keys(allValues ?? {})?.length > 0 && dataKeyDataValueFilters?.length > 0) {
			// prettier-ignore
			const [key, valueObject] = Object.entries(allValues)
            ?.find(
                (
                    [_, // unused key, entries usage is for later processing
                        { id } = {}] // = {} as default param for destructuring to prevent undefined error
                    ) => 
                    id === uuid
                )
            || [];

			const { label, value } = dataKeyDataValueFilters?.find(({ DataKeyID }) => DataKeyID == key) || {};

			const formattedDataValueSelections =
				dataKeyDataValueFilters
					// This comment exists to prevent eslint from making this unreadable.
					?.find(({ value }) => value == key)
					?.DataValues?.filter(({ value }) => valueObject?.values?.includes(value))
					?.map(({ label, value }) => ({ label, value })) || [];

			setDataKey({ label, value });
			setTimeout(() => setDataValues(formattedDataValueSelections), 1);
		}
	}, []);

	const handleDataKeyChange = useCallback(
		(dkv) => {
			setDataKey(dkv);
			// Remove any previous record associated with this uuid
			setValue((prevValue) => {
				if (Object.keys(prevValue ?? {}).length > 0) {
					const entries = Object.entries(prevValue)?.filter(([_, value]) => value.id !== uuid);
					return Object.fromEntries(entries);
				}
				return prevValue;
			});
		},
		[setValue, uuid]
	);

	// Dropdown handler
	const handleDataValuesChange = useCallback((dvs) => {
		setDataValues(dvs);
	}, []);

	// Options for key dropdown
	const dataKeyOptions = useMemo(() => {
		return (
			dataKeyDataValueFilters
				?.filter(({ value }) => {
					return !(allValues?.[value] && allValues?.[value]?.uuid !== uuid);
				})
				?.map(({ label, value }) => ({ label, value })) || []
		);
	}, [dataKeyDataValueFilters, uuid, allValues]);

	// Options for values dropdown
	const dataValueOptions = useMemo(() => {
		return (
			dataKeyDataValueFilters
				// This comment exists to prevent eslint from making this unreadable.
				?.find(({ value }) => value === dataKey?.value)
				?.DataValues?.map(({ label, value }) => ({ label, value })) || []
		);
	}, [dataKeyDataValueFilters, dataKey]);

	// Change what button is shown based on positioning in the list
	const buttonProps = useMemo(() => {
		if (totalNumFilters - 1 === position) {
			return {
				children: "Add Data Key",
				display: BUTTON_DISPLAY.ICON_BORDERED,
				icon: "icon-add",
				onClick: handleAdd
			};
		}
		return {
			children: "Delete Data Key",
			icon: "icon-delete",
			display: BUTTON_DISPLAY.ICON_BORDERED,
			onClick: () => {
				handleDelete(position);
				if (dataKey) {
					setValue((prevValue) => {
						const { [dataKey.value]: _, ...rest } = prevValue;
						return rest;
					});
				}
			}
		};
	}, [handleAdd, handleDelete, position, totalNumFilters, dataKey, setValue]);

	// Reset values when key changes
	useEffect(() => {
		setDataValues([]);
	}, [dataKey]);

	// Set values in the parent state when this component's values update appropriately.
	useEffect(() => {
		if (dataKey !== null && dataValues) {
			setValue((prevValue) => ({
				...prevValue,
				[dataKey.value]: {
					values: dataValues?.map(({ value }) => value) || [],
					id: uuid
				}
			}));
		}
	}, [dataKey, dataValues, setValue, uuid]);

	// Render
	return (
		<div className={`gos-pm-filters-sidesheet-keyValueFilter keyValueFilter-${position}`}>
			<div className={`gos-pm-filters-sidesheet-dataKey-container`}>
				<Dropdown
					className={"gos-pm-filters-sidesheet-dataKey-dropdown"}
					placeholder={"Select a key"}
					value={dataKey}
					options={dataKeyOptions}
					onChange={handleDataKeyChange}
					isClearable={true}
				>
					Data Key
				</Dropdown>
				<div className={"gos-pm-filters-sidesheet-keyValueFilter-button"}>
					<Button {...buttonProps} data-tracking-id-alt={totalNumFilters - 1 === position ? "add-data-key" : "delete-data-key"} />
				</div>
			</div>
			<Dropdown
				className={"gos-pm-filters-sidesheet-dataValue-dropdown"}
				placeholder={"Select a value"}
				multiple={true}
				isClearable={true}
				isSearchable={true}
				value={dataValues}
				options={dataValueOptions}
				onChange={handleDataValuesChange}
				display={OPTION_DISPLAY.PILL_INLINE}
				disabled={dataKey === null}
			>
				{dataKey?.label || "Data Value"}
			</Dropdown>
		</div>
	);
}

// Generate PropTypes for KeyValueFilter component
KeyValueFilter.propTypes = {
	position: PropTypes.number,
	totalNumFilters: PropTypes.number,
	uuid: PropTypes.string
};
